import React, { useEffect, useRef, useState } from 'react';
import { TaskPoint } from '../types';
import { MapPin, Wind, Zap, TrendingUp, Maximize2, X } from 'lucide-react';

interface TaskVisualizationProps {
  taskPoints: TaskPoint[] | null;
  originalTaskPoints: TaskPoint[] | null;
  isLoading: boolean;
}

const TaskVisualization: React.FC<TaskVisualizationProps> = ({ taskPoints, originalTaskPoints, isLoading }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const [showOsm, setShowOsm] = useState(false);
  const [showSatellite, setShowSatellite] = useState(false);
  const [showKk7, setShowKk7] = useState(false);
  const [satelliteVersion, setSatelliteVersion] = useState(0);
  const satelliteTileCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const [osmVersion, setOsmVersion] = useState(0);
  const osmTileCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const [kk7Version, setKk7Version] = useState(0);
  const kk7TileCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());

  useEffect(() => {
    if (taskPoints && taskPoints.length > 0 && canvasRef.current) {
      drawTask();
    }
  }, [taskPoints, originalTaskPoints, zoom, pan, showOsm, showSatellite, showKk7, satelliteVersion, osmVersion, kk7Version, isMapExpanded]);

  useEffect(() => {
    if (!isMapExpanded) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMapExpanded(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMapExpanded]);

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const a = 6378137;
    const f = 1 / 298.257223563;
    const b = (1 - f) * a;

    const toRad = (d: number) => (d * Math.PI) / 180;
    const L = toRad(lon2 - lon1);
    const U1 = Math.atan((1 - f) * Math.tan(toRad(lat1)));
    const U2 = Math.atan((1 - f) * Math.tan(toRad(lat2)));
    const sinU1 = Math.sin(U1);
    const cosU1 = Math.cos(U1);
    const sinU2 = Math.sin(U2);
    const cosU2 = Math.cos(U2);

    let lambda = L;
    let lambdaPrev = Number.POSITIVE_INFINITY;
    let iter = 0;

    let sinSigma = 0;
    let cosSigma = 0;
    let sigma = 0;
    let sinAlpha = 0;
    let cosSqAlpha = 0;
    let cos2SigmaM = 0;

    while (iter < 100 && Math.abs(lambda - lambdaPrev) > 1e-12) {
      iter += 1;
      lambdaPrev = lambda;
      const sinLambda = Math.sin(lambda);
      const cosLambda = Math.cos(lambda);

      const t1 = cosU2 * sinLambda;
      const t2 = cosU1 * sinU2 - sinU1 * cosU2 * cosLambda;
      sinSigma = Math.sqrt(t1 * t1 + t2 * t2);
      if (sinSigma === 0) return 0;
      cosSigma = sinU1 * sinU2 + cosU1 * cosU2 * cosLambda;
      sigma = Math.atan2(sinSigma, cosSigma);
      sinAlpha = (cosU1 * cosU2 * sinLambda) / sinSigma;
      cosSqAlpha = 1 - sinAlpha * sinAlpha;
      cos2SigmaM = cosSqAlpha !== 0 ? cosSigma - (2 * sinU1 * sinU2) / cosSqAlpha : 0;
      const C = (f / 16) * cosSqAlpha * (4 + f * (4 - 3 * cosSqAlpha));
      lambda =
        L +
        (1 - C) *
          f *
          sinAlpha *
          (sigma +
            C *
              sinSigma *
              (cos2SigmaM + C * cosSigma * (-1 + 2 * cos2SigmaM * cos2SigmaM)));
    }

    if (iter >= 100) {
      const R = 6371000;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const aa = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa));
      return R * c;
    }

    const uSq = (cosSqAlpha * (a * a - b * b)) / (b * b);
    const A = 1 + (uSq / 16384) * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
    const B = (uSq / 1024) * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));
    const deltaSigma = B * sinSigma * (cos2SigmaM + (B / 4) * (cosSigma * (-1 + 2 * cos2SigmaM * cos2SigmaM) - (B / 6) * cos2SigmaM * (-3 + 4 * sinSigma * sinSigma) * (-3 + 4 * cos2SigmaM * cos2SigmaM)));
    const s = b * A * (sigma - deltaSigma);
    return s;
  };

const calculateCylinderDistance = (lat1: number, lon1: number, radius1: number, lat2: number, lon2: number, radius2: number) => {
  const d = calculateDistance(lat1, lon1, lat2, lon2);
  const r1 = Math.max(0, radius1 || 0);
  const r2 = Math.max(0, radius2 || 0);
  const sum = r1 + r2;
  const diff = Math.abs(r1 - r2);

  if (d > sum) return d - sum;
  if (d < diff) return diff - d;
  return 0;
};

const calculateTaskDistance = (waypoints: TaskPoint[]) => {
  if (!waypoints || waypoints.length < 2) return 0;
  
  let totalDistance = 0;
  for (let i = 0; i < waypoints.length - 1; i++) {
    const current = waypoints[i];
    const next = waypoints[i + 1];
    
    const radius1 = current.name.includes('Thermal_') ? 100 : (current.radius_m || 500);
    const radius2 = next.name.includes('Thermal_') ? 100 : (next.radius_m || 500);
    
    totalDistance += calculateCylinderDistance(
      current.lat, current.lon, radius1,
      next.lat, next.lon, radius2
    );
  }
  
  return totalDistance; // Removed conversion to kilometers
};

const calculateContinuousPath = (waypoints: { x: number; y: number; radius_m: number }[]) => {
    const path: { x: number; y: number }[] = [];
    
    for (let i = 0; i < waypoints.length - 1; i++) {
      const current = waypoints[i];
      const next = waypoints[i + 1];
      
      const x1 = current.x;
      const y1 = current.y;
      const r1 = current.radius_m / 100; // Convert to canvas scale
      
      const x2 = next.x;
      const y2 = next.y;
      const r2 = next.radius_m / 100;
      
      // Calculate distance between centers
      const dx = x2 - x1;
      const dy = y2 - y1;
      const d = Math.sqrt(dx * dx + dy * dy);
      
      // If circles overlap or touch, just connect centers
      if (d <= r1 + r2) {
        if (i === 0) {
          path.push({ x: x1, y: y1 });
        }
        path.push({ x: x2, y: y2 });
        continue;
      }
      
      // Calculate the angle from current to next
      const angle = Math.atan2(dy, dx);
      
      // For continuous path, we need to determine if we should go "left" or "right" around the cylinder
      // based on the previous direction to maintain continuity
      
      let tangentAngle: number;
      let exitPoint: { x: number; y: number };
      let entryPoint: { x: number; y: number };
      
      if (i === 0) {
        // First segment: choose the shortest path
        const alpha = Math.asin((r2 - r1) / d);
        tangentAngle = angle + alpha;
        
        exitPoint = {
          x: x1 + r1 * Math.cos(tangentAngle),
          y: y1 + r1 * Math.sin(tangentAngle)
        };
        
        entryPoint = {
          x: x2 + r2 * Math.cos(tangentAngle),
          y: y2 + r2 * Math.sin(tangentAngle)
        };
        
        path.push({ x: x1, y: y1 });
        path.push(exitPoint);
      } else {
        // Subsequent segments: continue from previous exit point
        const previousPoint = path[path.length - 1];
        
        // Calculate angle from current center to previous exit point
        const prevAngle = Math.atan2(previousPoint.y - y1, previousPoint.x - x1);
        
        // Calculate the two possible tangent angles
        const alpha = Math.asin((r2 - r1) / d);
        const angle1 = angle + alpha;
        const angle2 = angle - alpha;
        
        // Choose the angle that's closer to the previous direction for continuity
        const diff1 = Math.abs(angle1 - prevAngle);
        const diff2 = Math.abs(angle2 - prevAngle);
        
        tangentAngle = diff1 < diff2 ? angle1 : angle2;
        
        // Exit point is where we leave the current cylinder
        exitPoint = {
          x: x1 + r1 * Math.cos(tangentAngle),
          y: y1 + r1 * Math.sin(tangentAngle)
        };
        
        // Entry point is where we enter the next cylinder
        entryPoint = {
          x: x2 + r2 * Math.cos(tangentAngle),
          y: y2 + r2 * Math.sin(tangentAngle)
        };
        
        // Replace the last point with the correct exit point
        path[path.length - 1] = exitPoint;
      }
      
      path.push(entryPoint);
      
      // Add the center of the last waypoint
      if (i === waypoints.length - 2) {
        path.push({ x: x2, y: y2 });
      }
    }
    
    return path;
  };

  const distanceXY = (a: { x: number; y: number }, b: { x: number; y: number }) => {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const closestPointOnSegment = (
    a: { x: number; y: number },
    b: { x: number; y: number },
    p: { x: number; y: number }
  ) => {
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const apx = p.x - a.x;
    const apy = p.y - a.y;
    const ab2 = abx * abx + aby * aby;
    if (ab2 === 0) return { x: a.x, y: a.y };
    let t = (apx * abx + apy * aby) / ab2;
    t = Math.max(0, Math.min(1, t));
    return { x: a.x + t * abx, y: a.y + t * aby };
  };

  const closestPointInDiscToPoint = (
    c: { x: number; y: number },
    r: number,
    p: { x: number; y: number }
  ) => {
    const vx = p.x - c.x;
    const vy = p.y - c.y;
    const d = Math.sqrt(vx * vx + vy * vy);
    if (d <= r || d === 0) return { x: p.x, y: p.y };
    const s = r / d;
    return { x: c.x + vx * s, y: c.y + vy * s };
  };

  const optimalTouchpoint = (
    c: { x: number; y: number },
    r: number,
    prev: { x: number; y: number } | null,
    next: { x: number; y: number } | null
  ) => {
    if (!prev && !next) return { x: c.x, y: c.y };
    if (!prev && next) return closestPointInDiscToPoint(c, r, next);
    if (!next && prev) return closestPointInDiscToPoint(c, r, prev);
    if (!prev || !next) return { x: c.x, y: c.y };

    const q = closestPointOnSegment(prev, next, c);
    if (distanceXY(q, c) <= r) {
      return q;
    }

    let bestTheta = 0;
    let bestVal = Infinity;
    for (let k = 0; k < 72; k++) {
      const theta = (k / 72) * Math.PI * 2;
      const p = { x: c.x + r * Math.cos(theta), y: c.y + r * Math.sin(theta) };
      const val = distanceXY(prev, p) + distanceXY(p, next);
      if (val < bestVal) {
        bestVal = val;
        bestTheta = theta;
      }
    }

    let step = (Math.PI * 2) / 72;
    let theta = bestTheta;
    for (let iter = 0; iter < 16; iter++) {
      const t1 = theta - step;
      const t2 = theta + step;
      const p1 = { x: c.x + r * Math.cos(t1), y: c.y + r * Math.sin(t1) };
      const p2 = { x: c.x + r * Math.cos(t2), y: c.y + r * Math.sin(t2) };
      const v1 = distanceXY(prev, p1) + distanceXY(p1, next);
      const v2 = distanceXY(prev, p2) + distanceXY(p2, next);
      if (v1 < bestVal) {
        bestVal = v1;
        theta = t1;
      } else if (v2 < bestVal) {
        bestVal = v2;
        theta = t2;
      } else {
        step *= 0.6;
      }
    }

    return { x: c.x + r * Math.cos(theta), y: c.y + r * Math.sin(theta) };
  };

  const calculateOptimizedTouchpoints = (waypoints: { x: number; y: number; radius_px: number }[]) => {
    if (!waypoints || waypoints.length === 0) return [] as { x: number; y: number }[];
    const centers = waypoints.map(w => ({ c: { x: w.x, y: w.y }, r: w.radius_px }));

    let pts = centers.map((node, i) => {
      const next = centers[Math.min(centers.length - 1, i + 1)].c;
      return closestPointInDiscToPoint(node.c, node.r, next);
    });

    for (let iter = 0; iter < 12; iter++) {
      for (let i = 0; i < centers.length; i++) {
        const node = centers[i];
        const prev = i > 0 ? pts[i - 1] : null;
        const next = i < centers.length - 1 ? pts[i + 1] : null;
        pts[i] = optimalTouchpoint(node.c, node.r, prev, next);
      }
    }

    return pts;
  };

  const calculateContinuousTouchDistanceM = (waypoints: TaskPoint[]) => {
    if (!waypoints || waypoints.length < 2) return 0;

    const pts = waypoints.map(w => ({
      lat: w.lat,
      lon: w.lon,
      radius_m: w.name.includes('Thermal_') ? 100 : (w.radius_m || 500)
    }));

    const midLat = (pts[0].lat + pts[pts.length - 1].lat) / 2;
    const R = 6371000;
    const cos = Math.cos((midLat * Math.PI) / 180);
    const toXY = (p: { lat: number; lon: number }) => ({
      x: (p.lon * Math.PI / 180) * R * cos,
      y: (p.lat * Math.PI / 180) * R
    });

    const centers = pts.map(p => ({ c: toXY(p), r: p.radius_m }));

    let touch = centers.map((node, i) => {
      const next = centers[Math.min(centers.length - 1, i + 1)].c;
      return closestPointInDiscToPoint(node.c, node.r, next);
    });

    for (let iter = 0; iter < 12; iter++) {
      for (let i = 0; i < centers.length; i++) {
        const prev = i > 0 ? touch[i - 1] : null;
        const next = i < centers.length - 1 ? touch[i + 1] : null;
        touch[i] = optimalTouchpoint(centers[i].c, centers[i].r, prev, next);
      }
    }

    let dist = 0;
    for (let i = 0; i < touch.length - 1; i++) {
      dist += distanceXY(touch[i], touch[i + 1]);
    }
    return dist;
  };

  const drawTask = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.imageSmoothingEnabled = true;
    try {
      // Not supported in all browsers
      (ctx as unknown as { imageSmoothingQuality?: string }).imageSmoothingQuality = 'high';
    } catch {
      // ignore
    }

    // Set canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight || 400;

    // Clear canvas
    ctx.fillStyle = '#f9fafb';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (!taskPoints || taskPoints.length === 0) return;

    // Calculate bounds (include original + optimized so both paths fit)
    const allPoints = [...taskPoints, ...((originalTaskPoints || []).filter(p => p && typeof p.lat === 'number' && typeof p.lon === 'number'))];
    const lats = allPoints.map(p => p.lat);
    const lons = allPoints.map(p => p.lon);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);

    const R = 6378137;
    const mercatorY = (lat: number) => {
      // Clamp to avoid infinity near the poles
      const clamped = Math.max(-85, Math.min(85, lat));
      const clampedRad = (clamped * Math.PI) / 180;
      return R * Math.log(Math.tan(Math.PI / 4 + clampedRad / 2));
    };
    const mercatorX = (lon: number) => {
      const lonRad = (lon * Math.PI) / 180;
      return R * lonRad;
    };

    const minMx = mercatorX(minLon);
    const maxMx = mercatorX(maxLon);
    const minMy = mercatorY(minLat);
    const maxMy = mercatorY(maxLat);

    // Add padding
    const padding = 40;
    const width = canvas.width - 2 * padding;
    const height = canvas.height - 2 * padding;

    const spanMx = (maxMx - minMx) || 1;
    const spanMy = (maxMy - minMy) || 1;
    const baseScale = Math.min(width / spanMx, height / spanMy);
    const scale = baseScale * zoom;

    // Center content inside the padded rectangle
    const contentW = spanMx * baseScale;
    const contentH = spanMy * baseScale;
    const offsetX = padding + (width - contentW) / 2;
    const offsetY = padding + (height - contentH) / 2;

    // Convert lat/lon to canvas coordinates with uniform Web Mercator scale
    const latToY = (lat: number) => {
      const my = mercatorY(lat);
      const baseY = offsetY + ((maxMy - my) * baseScale);
      return (baseY * zoom) + pan.y;
    };

    const lonToX = (lon: number) => {
      const mx = mercatorX(lon);
      const baseX = offsetX + ((mx - minMx) * baseScale);
      return (baseX * zoom) + pan.x;
    };

    const xToLon = (x: number) => {
      const baseX = (x - pan.x) / zoom;
      const mx = minMx + ((baseX - offsetX) / baseScale);
      return (mx / R) * (180 / Math.PI);
    };

    const yToLat = (y: number) => {
      const baseY = (y - pan.y) / zoom;
      const my = maxMy - ((baseY - offsetY) / baseScale);
      const latRad = 2 * Math.atan(Math.exp(my / R)) - Math.PI / 2;
      return (latRad * 180) / Math.PI;
    };

    const metersToPixels = (m: number) => m * scale;

    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

    const lonLatToTile = (lon: number, lat: number, z: number) => {
      const n = Math.pow(2, z);
      const x = ((lon + 180) / 360) * n;
      const latRad = (lat * Math.PI) / 180;
      const y =
        (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) /
        2 *
        n;
      return { x, y };
    };

    const tileToLonLat = (x: number, y: number, z: number) => {
      const n = Math.pow(2, z);
      const lon = (x / n) * 360 - 180;
      const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n)));
      const lat = (latRad * 180) / Math.PI;
      return { lon, lat };
    };

    const chooseTileZoom = (bounds: { minLat: number; maxLat: number; minLon: number; maxLon: number }) => {
      let z = 14;
      for (let i = 0; i < 10; i++) {
        const a = lonLatToTile(bounds.minLon, bounds.maxLat, z);
        const b = lonLatToTile(bounds.maxLon, bounds.minLat, z);
        const xMin = Math.floor(Math.min(a.x, b.x));
        const xMax = Math.floor(Math.max(a.x, b.x));
        const yMin = Math.floor(Math.min(a.y, b.y));
        const yMax = Math.floor(Math.max(a.y, b.y));
        const tiles = (xMax - xMin + 1) * (yMax - yMin + 1);
        if (tiles > 20 && z > 10) {
          z -= 1;
          continue;
        }
        if (tiles < 4 && z < 18) {
          z += 1;
          continue;
        }
        return z;
      }
      return z;
    };

    const drawTileLayer = (
      z: number,
      xMin: number,
      xMax: number,
      yMin: number,
      yMax: number,
      cache: Map<string, HTMLImageElement>,
      setVersion: React.Dispatch<React.SetStateAction<number>>,
      urlFor: (z0: number, x0: number, y0: number) => string,
      alpha: number
    ) => {
      for (let xT = xMin; xT <= xMax; xT++) {
        for (let yT = yMin; yT <= yMax; yT++) {
          const key = `${z}/${xT}/${yT}`;
          const cached = cache.get(key);
          if (!cached) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              cache.set(key, img);
              setVersion(v => v + 1);
            };
            img.onerror = () => {
              // ignore
            };
            img.src = urlFor(z, xT, yT);
            continue;
          }

          const nw = tileToLonLat(xT, yT, z);
          const se = tileToLonLat(xT + 1, yT + 1, z);
          const x0 = lonToX(nw.lon);
          const y0 = latToY(nw.lat);
          const x1 = lonToX(se.lon);
          const y1 = latToY(se.lat);

          ctx.globalAlpha = alpha;
          ctx.drawImage(cached, Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0), Math.abs(y1 - y0));
        }
      }
      ctx.globalAlpha = 1;
    };

    const clampLat = (lat: number) => Math.max(-85, Math.min(85, lat));

    // Compute visible bounds from the current canvas viewport (after zoom/pan)
    const cornerLons = [
      xToLon(0),
      xToLon(canvas.width),
      xToLon(0),
      xToLon(canvas.width)
    ];
    const cornerLats = [
      clampLat(yToLat(0)),
      clampLat(yToLat(0)),
      clampLat(yToLat(canvas.height)),
      clampLat(yToLat(canvas.height))
    ];

    const viewMinLon = Math.min(...cornerLons);
    const viewMaxLon = Math.max(...cornerLons);
    const viewMinLat = Math.min(...cornerLats);
    const viewMaxLat = Math.max(...cornerLats);

    const viewBounds = { minLat: viewMinLat, maxLat: viewMaxLat, minLon: viewMinLon, maxLon: viewMaxLon };
    const z = chooseTileZoom(viewBounds);
    const a = lonLatToTile(viewBounds.minLon, viewBounds.maxLat, z);
    const b = lonLatToTile(viewBounds.maxLon, viewBounds.minLat, z);
    const xMin = Math.floor(Math.min(a.x, b.x));
    const xMax = Math.floor(Math.max(a.x, b.x));
    const yMin = Math.floor(Math.min(a.y, b.y));
    const yMax = Math.floor(Math.max(a.y, b.y));

    const xMinC = clamp(xMin, 0, Math.pow(2, z) - 1);
    const xMaxC = clamp(xMax, 0, Math.pow(2, z) - 1);
    const yMinC = clamp(yMin, 0, Math.pow(2, z) - 1);
    const yMaxC = clamp(yMax, 0, Math.pow(2, z) - 1);

    if (showOsm) {
      drawTileLayer(
        z,
        xMinC,
        xMaxC,
        yMinC,
        yMaxC,
        osmTileCacheRef.current,
        setOsmVersion,
        (z0, x0, y0) => `/api/osm?z=${z0}&x=${x0}&y=${y0}`,
        1
      );
    }

    if (showSatellite) {
      drawTileLayer(
        z,
        xMinC,
        xMaxC,
        yMinC,
        yMaxC,
        satelliteTileCacheRef.current,
        setSatelliteVersion,
        (z0, x0, y0) => `/api/satellite?z=${z0}&x=${x0}&y=${y0}`,
        1
      );
    }

    if (showKk7) {
      drawTileLayer(
        z,
        xMinC,
        xMaxC,
        yMinC,
        yMaxC,
        kk7TileCacheRef.current,
        setKk7Version,
        (z0, x0, y0) => `/api/kk7tile?z=${z0}&x=${x0}&y=${y0}`,
        0.45
      );
    }

    // Draw task lines (continuous cylinder-touch shortest paths)
    ctx.lineWidth = 2;

    if (originalTaskPoints && originalTaskPoints.length > 1) {
      const originalCanvasWaypoints = originalTaskPoints.map(point => {
        const rM = point.name.includes('Thermal_') ? 100 : (point.radius_m || 500);
        return {
          x: lonToX(point.lon),
          y: latToY(point.lat),
          radius_px: Math.max(2, metersToPixels(rM))
        };
      });

      const originalTouchpoints = calculateOptimizedTouchpoints(originalCanvasWaypoints);
      if (originalTouchpoints.length > 1) {
        ctx.strokeStyle = '#22c55e';
        ctx.beginPath();
        ctx.moveTo(originalTouchpoints[0].x, originalTouchpoints[0].y);
        for (let i = 1; i < originalTouchpoints.length; i++) {
          ctx.lineTo(originalTouchpoints[i].x, originalTouchpoints[i].y);
        }
        ctx.stroke();
      }
    }

    const canvasWaypoints = taskPoints.map(point => {
      const rM = point.name.includes('Thermal_') ? 100 : (point.radius_m || 500);
      return {
        x: lonToX(point.lon),
        y: latToY(point.lat),
        radius_px: Math.max(2, metersToPixels(rM))
      };
    });

    const optimizedTouchpoints = calculateOptimizedTouchpoints(canvasWaypoints);

    if (optimizedTouchpoints.length > 1) {
      ctx.strokeStyle = '#3b82f6';
      ctx.beginPath();
      ctx.moveTo(optimizedTouchpoints[0].x, optimizedTouchpoints[0].y);
      for (let i = 1; i < optimizedTouchpoints.length; i++) {
        ctx.lineTo(optimizedTouchpoints[i].x, optimizedTouchpoints[i].y);
      }
      ctx.stroke();
    }

    // Draw cylinders and labels
    taskPoints.forEach((point, index) => {
      const x = lonToX(point.lon);
      const y = latToY(point.lat);

      // Determine point type and color
      let color = '#3b82f6'; // default blue
      let radius = 8;
      let label = point.name;

      if (point.name.includes('TAKEOFF') || point.kind === 'start') {
        color = '#10b981'; // green
        radius = 10;
      } else if (point.name.includes('SSS')) {
        color = '#f59e0b'; // amber
        radius = 10;
      } else if (point.name.includes('ESS')) {
        color = '#ef4444'; // red
        radius = 10;
      } else if (point.name.includes('Thermal_')) {
        color = '#f97316'; // orange
        radius = 6;
      }

      // Draw cylinder circle
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.3;
      
      // Determine cylinder size based on point type
      let cylinderRadius;
      if (point.name.includes('Thermal_')) {
        cylinderRadius = Math.max(4, metersToPixels(100));
      } else {
        const rM = point.radius_m || 500;
        cylinderRadius = Math.max(8, metersToPixels(rM));
      }
      
      ctx.beginPath();
      ctx.arc(x, y, cylinderRadius, 0, 2 * Math.PI);
      ctx.fill();

      // Draw center point
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, 2 * Math.PI);
      ctx.fill();

      // Draw label
      ctx.fillStyle = '#1f2937';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(label, x, y - radius - 5);

      // Draw point number
      ctx.fillStyle = 'white';
      ctx.font = 'bold 10px sans-serif';
      ctx.fillText((index + 1).toString(), x, y + 3);
    });

    // Draw legend
    const legendY = 20;
    const legendItems = [
      { color: '#10b981', label: 'TAKEOFF' },
      { color: '#f59e0b', label: 'SSS' },
      { color: '#ef4444', label: 'ESS' },
      { color: '#f97316', label: 'Thermal' },
      { color: '#3b82f6', label: 'Waypoint' }
    ];

    ctx.font = '11px sans-serif';
    legendItems.forEach((item, index) => {
      const legendX = canvas.width - 100;
      const y = legendY + index * 15;

      ctx.fillStyle = item.color;
      ctx.beginPath();
      ctx.arc(legendX, y, 4, 0, 2 * Math.PI);
      ctx.fill();

      ctx.fillStyle = '#4b5563';
      ctx.textAlign = 'left';
      ctx.fillText(item.label, legendX + 10, y + 3);
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Optimizing your task...</p>
          <p className="text-sm text-gray-500 mt-2">Analyzing thermal hotspots and terrain</p>
        </div>
      </div>
    );
  }

  if (!taskPoints || taskPoints.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="text-center text-gray-500">
          <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <p>No task to visualize</p>
          <p className="text-sm mt-2">Upload files and optimize to see results</p>
        </div>
      </div>
    );
  }

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prevZoom => Math.max(0.5, Math.min(3, prevZoom * delta)));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const renderMapControls = (variant: 'inline' | 'modal') => {
    return (
      <div className="flex items-center space-x-2">
        <button
          onClick={() => {
            setShowOsm(v => {
              const next = !v;
              if (next) setShowSatellite(false);
              return next;
            });
          }}
          className={`px-3 py-1 rounded text-sm ${showOsm ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700' : 'bg-gray-100 hover:bg-gray-200'}`}
        >
          OSM
        </button>
        <button
          onClick={() => {
            setShowSatellite(v => {
              const next = !v;
              if (next) setShowOsm(false);
              return next;
            });
          }}
          className={`px-3 py-1 rounded text-sm ${showSatellite ? 'bg-indigo-100 hover:bg-indigo-200 text-indigo-700' : 'bg-gray-100 hover:bg-gray-200'}`}
        >
          Satellite
        </button>
        <button
          onClick={() => setShowKk7(v => !v)}
          className={`px-3 py-1 rounded text-sm ${showKk7 ? 'bg-orange-100 hover:bg-orange-200 text-orange-700' : 'bg-gray-100 hover:bg-gray-200'}`}
        >
          KK7
        </button>
        <button
          onClick={() => setZoom(prevZoom => Math.max(0.5, prevZoom * 0.8))}
          className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm"
        >
          Zoom Out
        </button>
        <span className="text-sm text-gray-600">{Math.round(zoom * 100)}%</span>
        <button
          onClick={() => setZoom(prevZoom => Math.min(3, prevZoom * 1.2))}
          className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm"
        >
          Zoom In
        </button>
        <button
          onClick={resetView}
          className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-sm"
        >
          Reset
        </button>
        {variant === 'inline' && (
          <button
            onClick={() => setIsMapExpanded(true)}
            className="px-3 py-1 bg-gray-900 hover:bg-gray-800 text-white rounded text-sm inline-flex items-center space-x-2"
          >
            <Maximize2 className="h-4 w-4" />
            <span>Expand</span>
          </button>
        )}
      </div>
    );
  };

  const renderMapCanvas = (heightPx: number) => {
    return (
      <>
        <canvas
          ref={canvasRef}
          className="w-full h-full border border-gray-200 rounded cursor-move"
          style={{ height: `${heightPx}px` }}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        />
        <div className="mt-2 text-xs text-gray-500">
          Use mouse wheel to zoom, click and drag to pan
        </div>
      </>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Optimizing your task...</p>
          <p className="text-sm text-gray-500 mt-2">Analyzing thermal hotspots and terrain</p>
        </div>
      </div>
    );
  }

  if (!taskPoints || taskPoints.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="text-center text-gray-500">
          <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <p>No task to visualize</p>
          <p className="text-sm mt-2">Upload files and optimize to see results</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center space-x-2">
            <MapPin className="h-5 w-5 text-blue-600" />
            <h3 className="font-medium text-blue-900">Total Points</h3>
          </div>
          <p className="text-2xl font-bold text-blue-600 mt-1">{taskPoints.length}</p>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center space-x-2">
            <Wind className="h-5 w-5 text-green-600" />
            <h3 className="font-medium text-green-900">Thermal Points</h3>
          </div>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {taskPoints.filter(p => p.name.includes('Thermal_')).length}
          </p>
        </div>
        
        <div className="bg-orange-50 p-4 rounded-lg">
          <div className="flex items-center space-x-2">
            <Zap className="h-5 w-5 text-orange-600" />
            <h3 className="font-medium text-orange-900">Task Points</h3>
          </div>
          <p className="text-2xl font-bold text-orange-600 mt-1">
            {taskPoints.filter(p => !p.name.includes('Thermal_')).length}
          </p>
        </div>
      </div>

      {!isMapExpanded && (
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-medium text-gray-900">Task Visualization</h3>
            {renderMapControls('inline')}
          </div>
          {renderMapCanvas(400)}
        </div>
      )}

      {isMapExpanded && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
          onMouseDown={() => setIsMapExpanded(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-[90vw] h-[90vh] border flex flex-col"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="font-medium text-gray-900">Task Visualization</div>
              <button
                onClick={() => setIsMapExpanded(false)}
                className="p-2 rounded hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="h-5 w-5 text-gray-700" />
              </button>
            </div>
            <div className="px-4 py-3 border-b overflow-x-auto">
              {renderMapControls('modal')}
            </div>
            <div className="flex-1 p-4">
              {renderMapCanvas(Math.max(200, Math.floor(window.innerHeight * 0.9) - 170))}
            </div>
          </div>
        </div>
      )}

      {/* Distance Metrics */}
      {originalTaskPoints && taskPoints && (
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center space-x-2 mb-4">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            <h3 className="font-medium text-gray-900">Task Distance Analysis</h3>
          </div>

          {(() => {
            const originalDistanceM = calculateTaskDistance(originalTaskPoints);
            const optimizedDistanceM = calculateTaskDistance(taskPoints);
            const deltaM = optimizedDistanceM - originalDistanceM;
            const pct = originalDistanceM > 0 ? (deltaM / originalDistanceM) * 100 : 0;
            const increased = optimizedDistanceM > originalDistanceM;

            return (
              <>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Original Distance</div>
              <div className="text-lg font-bold text-gray-900">
                {(originalDistanceM / 1000).toFixed(1)} km
              </div>
            </div>
            
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Optimized Distance</div>
              <div className="text-lg font-bold text-blue-600">
                {(optimizedDistanceM / 1000).toFixed(1)} km
              </div>
            </div>
            
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Distance Change</div>
              <div className={`text-lg font-bold ${
                increased 
                  ? 'text-red-600' 
                  : 'text-green-600'
              }`}>
                {(Math.abs(deltaM) / 1000).toFixed(1)} km
              </div>
            </div>
            
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Percentage Change</div>
              <div className={`text-lg font-bold ${
                increased 
                  ? 'text-red-600' 
                  : 'text-green-600'
              }`}>
                {pct.toFixed(1)}%
              </div>
            </div>
          </div>
          
          <div className="mt-3 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                increased 
                  ? 'bg-red-500' 
                  : 'bg-green-500'
              }`} />
              <span>
                {increased 
                  ? 'Distance increased due to thermal waypoint insertion' 
                  : 'Distance reduced through optimal path planning'}
              </span>
            </div>
          </div>
              </>
            );
          })()}
        </div>
      )}

      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-medium text-gray-900 mb-4">Task Points</h3>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {taskPoints.map((point, index) => (
            <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${
                  point.name.includes('TAKEOFF') ? 'bg-green-500' :
                  point.name.includes('SSS') ? 'bg-amber-500' :
                  point.name.includes('ESS') ? 'bg-red-500' :
                  point.name.includes('Thermal_') ? 'bg-orange-500' : 'bg-blue-500'
                }`} />
                <span className="font-medium text-gray-900 text-sm">{point.name}</span>
                <span className="text-xs text-gray-500">
                  {point.lat.toFixed(4)}, {point.lon.toFixed(4)}
                </span>
              </div>
              <div className="text-xs text-gray-500">
                {point.radius_m ? `${point.radius_m}m` : ''}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TaskVisualization;
